import React, { useEffect, useState } from 'react';
import clinicService from '../../../../../services/clinic.service';
import { Collapse, Pagination, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Loading from '../../../../common/Loading';
const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

function FeedbackTable({ clinicid }) {
    const [feedbacks, setFeedbacks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});
    const [copiedItems, setCopiedItems] = useState({});

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalElements, setTotalElements] = useState(0);

    const pageSizeOptions = [5, 10, 20, 50];

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const getFeedbacks = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const res = await clinicService.getReputationFeedbacks(clinicid, currentPage - 1, itemsPerPage, {
                    signal: controller.signal,
                });

                if (isMounted) {
                    setFeedbacks(res.data.content);
                    setTotalElements(res.data.totalElements);
                }
            } catch (err) {
                if (err.name !== 'AbortError' && isMounted) {
                    console.error('Fetch error:', err);
                    setError('Unable to load feedbacks. Please try again.');
                    setFeedbacks([]);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        getFeedbacks();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [clinicid, currentPage, itemsPerPage]);

    const toggleRow = (id) => {
        setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const copyToClipboard = (text, itemType, itemId) => {
        navigator.clipboard.writeText(text).then(() => {
            const key = `${itemType}-${itemId}`;
            setCopiedItems((prev) => ({ ...prev, [key]: true }));
            setTimeout(() => {
                setCopiedItems((prev) => ({ ...prev, [key]: false }));
            }, 2000);
        });
    };

    const getFeedbackBadge = (feedbackValue) => {
        switch (feedbackValue) {
            case 2:
                return (
                    <span className="reputation-status-badge reputation-feedback-badge--positive">
                        <i className="bi bi-emoji-smile"></i> Positive
                    </span>
                );
            case 1:
                return (
                    <span className="reputation-status-badge reputation-feedback-badge--neutral">
                        <i className="bi bi-emoji-neutral"></i> Neutral
                    </span>
                );
            case 0:
                return (
                    <span className="reputation-status-badge reputation-feedback-badge--negative">
                        <i className="bi bi-emoji-frown"></i> Negative
                    </span>
                );
            default:
                return <span className="reputation-status-badge reputation-status-badge--inactive">Unknown</span>;
        }
    };

    const getFeedbackLabel = (feedbackValue) => {
        if (feedbackValue === 2) return 'Positive';
        if (feedbackValue === 1) return 'Neutral';
        if (feedbackValue === 0) return 'Negative';
        return 'Unknown';
    };

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

    const totalPages = Math.ceil(totalElements / itemsPerPage);
    const startItem = feedbacks.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
    const endItem = Math.min(currentPage * itemsPerPage, totalElements);

    const paginationItems = [];
    for (let number = 1; number <= totalPages; number++) {
        paginationItems.push(
            <Pagination.Item key={number} active={number === currentPage} onClick={() => setCurrentPage(number)}>
                {number}
            </Pagination.Item>
        );
    }

    return (
        <div className="notifications-panel">
            <div className="notifications-panel__header">
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-star"></i>
                    Patient Feedbacks
                    {!isLoading && (
                        <span className="text-muted small fw-normal">
                            ({totalElements} record{totalElements !== 1 ? 's' : ''})
                        </span>
                    )}
                </span>
            </div>

            <div className="notifications-panel__body">
                <div className="reputation-toolbar">
                    <div className="d-flex align-items-center gap-2">
                        <span>Show</span>
                        <select
                            className="form-select form-select-sm reputation-page-size"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            disabled={isLoading}
                        >
                            {pageSizeOptions.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                        <span>entries</span>
                    </div>
                    <div>
                        Total: <strong>{totalElements}</strong>
                    </div>
                </div>

                {error && !isLoading && (
                    <div className="alert alert-warning border-0 py-2 px-3 small mb-3">{error}</div>
                )}

                {isLoading ? (
                    <Loading />
                ) : (
                    <>
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover align-middle mb-0 process-queue-table reputation-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <i className="bi bi-person"></i> Patient
                                        </th>
                                        <th>
                                            <i className="bi bi-star"></i> Feedback
                                        </th>
                                        <th>
                                            <i className="bi bi-calendar"></i> Date
                                        </th>
                                        <th className="text-end">
                                            <i className="bi bi-gear"></i> Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {feedbacks.length > 0 ? (
                                        feedbacks.map((feedback) => (
                                            <React.Fragment key={feedback.id}>
                                                <tr className="reputation-row">
                                                    <td>
                                                        <span className="reputation-name">{feedback.patientName}</span>
                                                    </td>
                                                    <td>{getFeedbackBadge(feedback.feedback)}</td>
                                                    <td className="reputation-date">{formatDate(feedback.createdAt)}</td>
                                                    <td className="text-end">
                                                        <ActionTooltipButton
                                                            tooltip={expandedRows[feedback.id] ? 'Collapse details' : 'View details'}
                                                            tooltipId={`feedback-expand-${feedback.id}`}
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => toggleRow(feedback.id)}
                                                            aria-controls={`collapse-feedback-${feedback.id}`}
                                                            aria-expanded={expandedRows[feedback.id]}
                                                        >
                                                            <i className={`bi ${expandedRows[feedback.id] ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                                        </ActionTooltipButton>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="4" className="p-0 border-0">
                                                        <Collapse in={expandedRows[feedback.id]}>
                                                            <div id={`collapse-feedback-${feedback.id}`} className="reputation-detail-panel">
                                                                <div className="row g-3">
                                                                    <div className="col-md-6">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Feedback ID</span>
                                                                            <div className="notifications-info-value d-flex align-items-center gap-2">
                                                                                <span className="font-monospace small">{feedback.id}</span>
                                                                                <ActionTooltipButton
                                                                                    tooltip={copiedItems[`feedback-${feedback.id}`] ? 'Copied!' : 'Copy feedback ID'}
                                                                                    tooltipId={`copy-feedback-${feedback.id}`}
                                                                                    variant="outline-secondary"
                                                                                    size="sm"
                                                                                    className="clinic-copy-btn p-1"
                                                                                    onClick={() => copyToClipboard(feedback.id, 'feedback', feedback.id)}
                                                                                >
                                                                                    <i className={`bi ${copiedItems[`feedback-${feedback.id}`] ? 'bi-check text-success' : 'bi-copy'}`}></i>
                                                                                </ActionTooltipButton>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Clinic ID</span>
                                                                            <div className="notifications-info-value d-flex align-items-center gap-2">
                                                                                <span className="font-monospace small">{feedback.clinicId}</span>
                                                                                <ActionTooltipButton
                                                                                    tooltip={copiedItems[`clinic-${feedback.id}`] ? 'Copied!' : 'Copy clinic ID'}
                                                                                    tooltipId={`copy-clinic-${feedback.id}`}
                                                                                    variant="outline-secondary"
                                                                                    size="sm"
                                                                                    className="clinic-copy-btn p-1"
                                                                                    onClick={() => copyToClipboard(feedback.clinicId, 'clinic', feedback.id)}
                                                                                >
                                                                                    <i className={`bi ${copiedItems[`clinic-${feedback.id}`] ? 'bi-check text-success' : 'bi-copy'}`}></i>
                                                                                </ActionTooltipButton>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Patient ID</span>
                                                                            <div className="notifications-info-value d-flex align-items-center gap-2">
                                                                                <span className="font-monospace small">{feedback.patientId}</span>
                                                                                <ActionTooltipButton
                                                                                    tooltip={copiedItems[`patient-${feedback.id}`] ? 'Copied!' : 'Copy patient ID'}
                                                                                    tooltipId={`copy-patient-${feedback.id}`}
                                                                                    variant="outline-secondary"
                                                                                    size="sm"
                                                                                    className="clinic-copy-btn p-1"
                                                                                    onClick={() => copyToClipboard(feedback.patientId, 'patient', feedback.id)}
                                                                                >
                                                                                    <i className={`bi ${copiedItems[`patient-${feedback.id}`] ? 'bi-check text-success' : 'bi-copy'}`}></i>
                                                                                </ActionTooltipButton>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Patient Name</span>
                                                                            <span className="notifications-info-value">{feedback.patientName}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Feedback Type</span>
                                                                            <span className="notifications-info-value">{getFeedbackLabel(feedback.feedback)}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Created At</span>
                                                                            <span className="notifications-info-value">{formatDate(feedback.createdAt)}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Collapse>
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="p-0 border-0">
                                                <div className="reputation-empty">
                                                    <i className="bi bi-star"></i>
                                                    No patient feedbacks found.
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {feedbacks.length > 0 && (
                            <div className="reputation-footer">
                                <div>
                                    Showing {startItem} to {endItem} of {totalElements} entries
                                </div>
                                {totalPages > 1 && (
                                    <Pagination className="mb-0">
                                        <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                                        <Pagination.Prev
                                            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                            disabled={currentPage === 1}
                                        />
                                        {paginationItems}
                                        <Pagination.Next
                                            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default FeedbackTable;
